import _ from 'lodash';
import { Store } from 'pullstate';
import { ApiResponseType } from '../../types';
import { client } from '../../utils/axios-client';
import { v4 as uuid } from 'uuid';
import { Filter } from '../filters/FilterTypes';

export type Playbook = {
    id: string;
    name: string;
    libraries: PlaybookLibrary[];
    templates: CadenceTemplate[];
    cadences: PlaybookCadence[];
    miCount: number;
    mqlCount: number;
    dcCount: number;
    opportunityCount: number;
    executedCount: number;
};

export type PlaybookCadence = {
    id: string;
    name: string;
    cadenceTypeName: string;
    cadenceTypeId: string;
    //activeStartDate: string,
    //activeEndDate: string,
    startStageId: string;
    startStageName: string;
    endStageId: string;
    endSTageName: string;
    endActionId: string;
    endActionName: string;
    templateId: string;
    actionId: string;
    actionName: string;
    miCount: number;
    mqlCount: number;
    dcCount: number;
    opportunityCount: number;
    executedCount: number;
    nextCadenceId: string;
};

export type PlaybookLibrary = {
    id: string;
    name: string;
    content: string;
};

export type CadenceTemplate = {
    id: string;
    name: string;
    deliverTo: string;
    subject: string;
    templateBody: string;
    complianceEmail: string;
    templateTypeId: string;
    templateType: string;
    allowSelection: boolean;
    variants: CadenceTemplateVariant[];
    attachments: CadenceAttachment[];
};

export type CadenceTemplateVariant = {
    id: string;
    name: string;
    subject: string;
    deliverTo: string;
    templateBody: string;
    complianceEmail: string;
    templateTypeId: string;
    templateType: string;
    parentId: null | string;
    allowSelection: boolean;
    attachments: CadenceAttachment[];
};

export type CadenceAttachment = {
    id: string;
    documentName: string;
    fileSize: number;
    contentType: string;
};

const PlaybookStore = new Store({
    playbooks: [] as Playbook[],
    filteredPlaybooks: [] as Playbook[],
    selectedPlaybook: null as null | Playbook,
    selectedLibrary: null as null | PlaybookLibrary,
    selectedTemplate: null as null | CadenceTemplate,
    selectedTemplateVariant: null as null | CadenceTemplateVariant,
    selectedFilter: null as null | Filter,
    selectedCadence: null as null | PlaybookCadence,
});

export type CadencePlaybookStep = {
    id: string;
    name: string;
    miCount: number;
    mqlCount: number;
    dcCount: number;
    opportunityCount: number;
    executedCount: number;
    proccessWhen: number;
    processOrder: number;
    autoSend: boolean;
    currentActivityId: string;
    currentActivityName: string;
    cadenceTemplateId: string;
    cadenceTemplateName: string;
    [key: string]: any;
};

export const loadPlaybooks = async (filter: string) => {
    const response = await client.get('/api/playbooks');

    PlaybookStore.update((s, o) => {
        s.playbooks = response.data;

        const filteredRecords = filterPlaybooks(response.data, filter);

        s.filteredPlaybooks = filteredRecords;

        if (o.selectedPlaybook) {
            const pb = filteredRecords.find((p) => p.id === o.selectedPlaybook?.id);
            s.selectedPlaybook = pb ?? null;

            if (o.selectedTemplate && pb) {
                const currentTemplate = pb.templates.find((p) => p.id === o.selectedTemplate?.id) ?? null;

                s.selectedTemplate = currentTemplate;

                if (o.selectedTemplateVariant && currentTemplate) {
                    s.selectedTemplateVariant =
                        currentTemplate?.variants.find((p) => p.id === o.selectedTemplateVariant?.id) ?? null;
                }
            }

            //if (pb ) {
            //    s.selectedLibrary = pb.libraries.find(p => p.id == o.selectedLibrary?.id) ?? null;
            //}
        }
    });
};

export const filterPlaybooks = (records: Playbook[], filter: string) => {
    if (!_.isEmpty(filter)) {
        return records.filter((s) => s.name.toLowerCase().includes(filter.toLowerCase()));
    } else {
        return records;
    }
};

export const togglePlaybookStatus = async (row: Playbook, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/status', row);

    loadPlaybooks(filter);
};

export const renamePlaybook = async (row: Playbook, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/name', row);

    loadPlaybooks(filter);
    return response.data;
};

export const savePlaybook = async (row: Playbook, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks', row);
    await loadPlaybooks(filter);

    return response.data;
};

export const renamePlaybookLibrary = async (row: PlaybookLibrary, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/library/name', row);

    loadPlaybooks(filter);

    PlaybookStore.update((s) => {
        s.selectedLibrary = null;
    });

    return response.data;
};

export const saveLibrary = async (row: PlaybookLibrary, playbook: Playbook, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/library', {
        id: row.id,
        name: row.name,
        isActive: true,
        content: '',
        playbookId: playbook.id,
    });
    await loadPlaybooks(filter);

    //PlaybookStore.update(s => {
    //    s.selectedLibrary = _.cloneDeep(row);
    //})

    return response.data;
};

export const saveLibraryContent = async (row: PlaybookLibrary, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/library', row);

    await loadPlaybooks(filter);

    //PlaybookStore.update(s => {
    //    s.selectedLibrary = _.cloneDeep(row);
    //})

    return response.data;
};

export const toggleLibraryStatus = async (row: PlaybookLibrary, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/library/status', row);
    await loadPlaybooks(filter);
};

export const renamePlaybookTemplate = async (row: CadenceTemplate, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/templates/name', row);

    loadPlaybooks(filter);

    PlaybookStore.update((s) => {
        //       s.selectedTemplate = null;
    });

    return response.data;
};

export const saveTemplate = async (row: CadenceTemplate, playbook: Playbook, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/templates', {
        ...row,
        playbookId: playbook.id,
    });
    await loadPlaybooks(filter);

    PlaybookStore.update((s) => {
        //s.selectedTemplate = _.cloneDeep(row);
    });

    return response.data;
};

export const saveTemplateVariant = async (
    row: CadenceTemplateVariant,
    playbook: Playbook,
    parent: CadenceTemplate,
    filter: string,
) => {
    const record = {
        id: row.id,
        allowSelection: row.allowSelection,
        name: row.name,
        isActive: true,
        playbookId: playbook.id,
        attachments: [],
        deliverTo: row.deliverTo,
        templateBody: row.templateBody,
        parentId: parent.id,
        templateTypeId: row.templateTypeId,
        templateType: '',
        subject: row.subject,
        complianceEmail: '',
    } as CadenceTemplateVariant;

    const response = await client.post<ApiResponseType>('/api/playbooks/templates', record);
    await loadPlaybooks(filter);

    PlaybookStore.update((s, o) => {
        //if (o.selectedTemplate) {
        //    s.selectedTemplate = { ...o.selectedTemplate, variants: [...o.selectedTemplate?.variants, record] };
        //}
        //s.selectedTemplate = null;
    });

    return response.data;
};

export const toggleTemplateStatus = async (row: CadenceTemplate, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/templates/status', row);
    await loadPlaybooks(filter);
};

export const toggleTemplateVariantStatus = async (row: CadenceTemplateVariant, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/templates/status', row);
    await loadPlaybooks(filter);
};

export const renamePlaybookTemplateVariant = async (row: CadenceTemplateVariant, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/templates/name', row);

    loadPlaybooks(filter);

    PlaybookStore.update((s) => {
        //s.selectedTemplateVariant = null;
    });

    return response.data;
};

export const saveCadence = async (row: PlaybookCadence, playbook: Playbook, filter: string) => {
    const response = await client.post<ApiResponseType>('/api/playbooks/cadence', row);
    await loadPlaybooks(filter);

    //PlaybookStore.update(s => {
    //    s.selectedLibrary = _.cloneDeep(row);
    //})

    return response.data;
};

export { PlaybookStore };
